// Interview Prep: what the user knows, one score per topic (0 to 1).
//
// Scores aren't stored. They're replayed from the setup ratings and every
// practice tap, story and debrief, so a rule change applies to old data too
// and nothing drifts out of sync.
//
//   users/{uid}/prep/settings      ratings, completedAt (the setup day)
//   users/{uid}/prepResults/{id}   every solved / hints / missed tap

import { STORY_TOPICS, TOPICS, type Difficulty, type TopicId } from "../data/prep/topics";

// --- Days (yyyy-mm-dd, local) ----------------------------------------------------

const DAY_MS = 86400000;

/** Whole days as UTC midnights, so DST never shifts a count */
const dayNumber = (day: string) => {
  const [y, m, d] = day.split("-").map(Number);
  return Date.UTC(y, m - 1, d) / DAY_MS;
};

export function addDays(day: string, n: number): string {
  return new Date((dayNumber(day) + n) * DAY_MS).toISOString().slice(0, 10);
}

/** b − a in days */
export function daysBetween(a: string, b: string): number {
  return dayNumber(b) - dayNumber(a);
}

export type Weekday = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";
const WEEKDAYS: Weekday[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function weekday(day: string): Weekday {
  return WEEKDAYS[new Date(dayNumber(day) * DAY_MS).getUTCDay()];
}

// --- Skills ----------------------------------------------------------------------

export type Rating = "shaky" | "okay" | "solid";
export type PracticeResult = "solved" | "hints" | "missed";

export const RATING_SCORE: Record<Rating, number> = { shaky: 0.3, okay: 0.55, solid: 0.8 };
/** Behavioral themes start here: no story written yet */
export const STORY_START = 0.3;

const SOLVED_GAIN: Record<Difficulty, number> = { Easy: 0.03, Medium: 0.05, Hard: 0.07 };
const HINTS_GAIN = 0.01;
const MISSED_LOSS = -0.05;
const STRUGGLED_LOSS = -0.1;
const STORY_GAIN = 0.05;
/** A topic untouched this long starts slipping, a little each week, never by more than DRIFT_MAX */
const DRIFT_AFTER_DAYS = 21;
const DRIFT_PER_WEEK = 0.02;
const DRIFT_MAX = 0.15;
const DRIFT_FLOOR = 0.1;

export type SkillEvent =
  | { kind: "result"; day: string; topic: TopicId; difficulty: Difficulty; result: PracticeResult }
  /** Debrief: "I got stuck on X" */
  | { kind: "struggled"; day: string; topic: TopicId }
  | { kind: "story"; day: string; topic: TopicId };

export type Skills = Record<TopicId, number>;

/** 0 to 1, rounded so 0.55 + 0.05 stays 0.6 */
const clamp = (n: number) => Math.round(Math.min(1, Math.max(0, n)) * 1000) / 1000;

function eventDelta(event: SkillEvent): number {
  if (event.kind === "struggled") return STRUGGLED_LOSS;
  if (event.kind === "story") return STORY_GAIN;
  if (event.result === "solved") return SOLVED_GAIN[event.difficulty];
  return event.result === "hints" ? HINTS_GAIN : MISSED_LOSS;
}

/** Every topic's score today. Unrated coding topics count as "okay". */
export function skillsFrom(
  ratings: Partial<Record<TopicId, Rating>>,
  setupDay: string,
  events: SkillEvent[],
  today: string
): Skills {
  const stories = new Set<TopicId>(STORY_TOPICS.map((t) => t.id));
  const skills = {} as Skills;
  const touched = {} as Record<TopicId, string>;
  for (const { id } of TOPICS) {
    const rating = ratings[id];
    skills[id] = rating ? RATING_SCORE[rating] : stories.has(id) ? STORY_START : RATING_SCORE.okay;
    touched[id] = setupDay;
  }
  const sorted = [...events].sort((a, b) => daysBetween(b.day, a.day));
  for (const event of sorted) {
    if (!(event.topic in skills) || event.day > today) continue;
    skills[event.topic] = clamp(skills[event.topic] + eventDelta(event));
    if (event.day > touched[event.topic]) touched[event.topic] = event.day;
  }
  for (const { id } of TOPICS) {
    const idle = daysBetween(touched[id], today) - DRIFT_AFTER_DAYS;
    if (idle <= 0 || skills[id] <= DRIFT_FLOOR) continue;
    const drift = Math.min(DRIFT_MAX, Math.ceil(idle / 7) * DRIFT_PER_WEEK);
    skills[id] = Math.max(DRIFT_FLOOR, skills[id] - drift);
  }
  return skills;
}
